import { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '@/lib/supabase/cliente'
import type { Apu, AiuApu } from './tipos'
import { recalcularApu } from './tipos'
import { APUS_INICIALES } from './datosInicialesApus'

const sb: any = supabase
const CLAVE_LOCAL = 'mega_banco_apus_v1'

export function generarSiguienteCodigoApu(lista: Apu[]): string {
  let maxNum = 0
  for (const a of lista) {
    const match = a.codigo.match(/(\d+)$/)
    if (match) {
      const n = parseInt(match[1], 10)
      if (!isNaN(n) && n > maxNum) maxNum = n
    }
  }
  const siguiente = maxNum + 1
  return `APU-${String(siguiente).padStart(3, '0')}`
}

function guardarLocal(apus: Apu[]) {
  try {
    localStorage.setItem(CLAVE_LOCAL, JSON.stringify(apus))
  } catch (err) {
    console.error('Error guardando APUs localmente:', err)
  }
}

function cargarLocal(): Apu[] {
  try {
    const raw = localStorage.getItem(CLAVE_LOCAL)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed
          .filter((a) => Boolean(a && a.activo !== false))
          .map((a) => recalcularApu(a))
      }
    }
  } catch (err) {
    console.error('Error leyendo APUs de localStorage:', err)
  }
  return APUS_INICIALES
}

export function useBancoApus() {
  const [apus, setApus] = useState<Apu[]>(() => cargarLocal())
  const [cargando, setCargando] = useState(false)
  const [busqueda, setBusqueda] = useState('')
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState<string>('todas')
  const [criterioOrden, setCriterioOrden] = useState<'codigo' | 'categoria' | 'precio_desc' | 'precio_asc'>('codigo')
  const [filtroProyecto, setFiltroProyecto] = useState<string>('todos')

  const siguienteCodigo = useMemo(() => generarSiguienteCodigoApu(apus), [apus])

  // Cargar desde Supabase
  const sincronizarDesdeServidor = useCallback(async () => {
    try {
      setCargando(true)
      const { data, error } = await sb
        .from('plan_apus')
        .select('*')
        .eq('activo', true)
        .order('categoria', { ascending: true })

      if (!error && Array.isArray(data)) {
        const mapeados = data.map((row: any) =>
          recalcularApu({
            id: row.id,
            codigo: row.codigo,
            nombre: row.nombre,
            descripcion: row.descripcion || '',
            unidad: row.unidad || 'UND',
            categoria: row.categoria || 'General',
            equipos: Array.isArray(row.equipos) ? row.equipos : [],
            materiales: Array.isArray(row.materiales) ? row.materiales : [],
            transportes: Array.isArray(row.transportes) ? row.transportes : [],
            mano_obra: Array.isArray(row.mano_obra) ? row.mano_obra : [],
            aiu: {
              porcentaje_admin: Number(row.porcentaje_admin || 10),
              porcentaje_imprevistos: Number(row.porcentaje_imprevistos || 0),
              porcentaje_utilidad: Number(row.porcentaje_utilidad || 3),
              porcentaje_iva_utilidad: Number(row.porcentaje_iva_utilidad || 19),
            },
            activo: Boolean(row.activo),
            created_at: row.created_at,
            updated_at: row.updated_at,
          })
        )
        setApus(mapeados)
        guardarLocal(mapeados)
      }
    } catch (err) {
      console.warn('Usando catálogo local de APUs:', err)
    } finally {
      setCargando(false)
    }
  }, [])

  useEffect(() => {
    void sincronizarDesdeServidor()

    const onApusActualizado = () => {
      setApus(cargarLocal())
    }
    window.addEventListener('mega:banco-apus-actualizado', onApusActualizado)
    return () => {
      window.removeEventListener('mega:banco-apus-actualizado', onApusActualizado)
    }
  }, [sincronizarDesdeServidor])

  // Crear un APU
  const crearApu = useCallback(
    (nuevo: Partial<Apu>): Apu => {
      const id = nuevo.id || crypto.randomUUID()
      const codigo = nuevo.codigo || generarSiguienteCodigoApu(apus)
      const base: Apu = recalcularApu({
        id,
        codigo,
        nombre: nuevo.nombre || 'Nueva Actividad APU',
        descripcion: nuevo.descripcion || '',
        unidad: nuevo.unidad || 'UND',
        categoria: nuevo.categoria || 'General',
        proyecto_id: nuevo.proyecto_id || null,
        proyecto_nombre: nuevo.proyecto_nombre || null,
        equipos: nuevo.equipos || [],
        materiales: nuevo.materiales || [],
        transportes: nuevo.transportes || [],
        mano_obra: nuevo.mano_obra || [],
        aiu: nuevo.aiu || {
          porcentaje_admin: 10,
          porcentaje_imprevistos: 0,
          porcentaje_utilidad: 3,
          porcentaje_iva_utilidad: 19,
        },
        activo: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })

      setApus((prev) => {
        const actualizados = [base, ...prev]
        guardarLocal(actualizados)
        try {
          window.dispatchEvent(new CustomEvent('mega:banco-apus-actualizado'))
        } catch {}
        return actualizados
      })

      // Sincronizar en BD
      void (async () => {
        try {
          await sb.from('plan_apus').insert({
            id: base.id,
            codigo: base.codigo,
            nombre: base.nombre,
            descripcion: base.descripcion,
            unidad: base.unidad,
            categoria: base.categoria,
            equipos: base.equipos,
            materiales: base.materiales,
            transportes: base.transportes,
            mano_obra: base.mano_obra,
            porcentaje_admin: base.aiu.porcentaje_admin,
            porcentaje_imprevistos: base.aiu.porcentaje_imprevistos,
            porcentaje_utilidad: base.aiu.porcentaje_utilidad,
            porcentaje_iva_utilidad: base.aiu.porcentaje_iva_utilidad,
            costo_equipos: base.costo_equipos,
            costo_materiales: base.costo_materiales,
            costo_transportes: base.costo_transportes,
            costo_mano_obra: base.costo_mano_obra,
            horas_hombre_unidad: base.horas_hombre_unidad,
            costo_directo: base.costo_directo,
            valor_aiu: base.valor_aiu,
            precio_unitario_total: base.precio_unitario_total,
            activo: true,
          })
        } catch (err) {
          console.error('Error insertando APU en Supabase:', err)
        }
      })()

      return base
    },
    []
  )

  // Actualizar un APU completo
  const actualizarApu = useCallback(
    (id: string, cambios: Partial<Apu>) => {
      setApus((prev) => {
        const actualizados = prev.map((item) => {
          if (item.id !== id) return item
          return recalcularApu({
            ...item,
            ...cambios,
            aiu: {
              ...item.aiu,
              ...(cambios.aiu || {}),
            },
          })
        })
        guardarLocal(actualizados)

        // Sincronizar BD
        const modificado = actualizados.find((a) => a.id === id)
        if (modificado) {
          void (async () => {
            try {
              await sb
                .from('plan_apus')
                .update({
                  nombre: modificado.nombre,
                  descripcion: modificado.descripcion,
                  unidad: modificado.unidad,
                  categoria: modificado.categoria,
                  equipos: modificado.equipos,
                  materiales: modificado.materiales,
                  transportes: modificado.transportes,
                  mano_obra: modificado.mano_obra,
                  porcentaje_admin: modificado.aiu.porcentaje_admin,
                  porcentaje_imprevistos: modificado.aiu.porcentaje_imprevistos,
                  porcentaje_utilidad: modificado.aiu.porcentaje_utilidad,
                  porcentaje_iva_utilidad: modificado.aiu.porcentaje_iva_utilidad,
                  costo_equipos: modificado.costo_equipos,
                  costo_materiales: modificado.costo_materiales,
                  costo_transportes: modificado.costo_transportes,
                  costo_mano_obra: modificado.costo_mano_obra,
                  horas_hombre_unidad: modificado.horas_hombre_unidad,
                  costo_directo: modificado.costo_directo,
                  valor_aiu: modificado.valor_aiu,
                  precio_unitario_total: modificado.precio_unitario_total,
                  updated_at: new Date().toISOString(),
                })
                .eq('id', id)
            } catch (err) {
              console.error('Error actualizando APU en Supabase:', err)
            }
          })()
        }

        return actualizados
      })
    },
    []
  )

  // Modificar rápidamente solo el AIU al tocar el APU
  const actualizarAiuApu = useCallback(
    (id: string, nuevoAiu: Partial<AiuApu>) => {
      actualizarApu(id, {
        aiu: {
          porcentaje_admin: nuevoAiu.porcentaje_admin ?? 10,
          porcentaje_imprevistos: nuevoAiu.porcentaje_imprevistos ?? 0,
          porcentaje_utilidad: nuevoAiu.porcentaje_utilidad ?? 3,
          porcentaje_iva_utilidad: nuevoAiu.porcentaje_iva_utilidad ?? 19,
        },
      })
    },
    [actualizarApu]
  )

  // Anular APU (borrado lógico / soft-delete: activo = false)
  const eliminarApu = useCallback((id: string) => {
    setApus((prev) => {
      const actualizados = prev.filter((a) => a.id !== id)
      guardarLocal(actualizados)
      return actualizados
    })

    setTimeout(() => {
      try {
        window.dispatchEvent(new CustomEvent('mega:banco-apus-actualizado'))
      } catch {}
    }, 50)

    void (async () => {
      try {
        const { error } = await sb
          .from('plan_apus')
          .update({
            activo: false,
            updated_at: new Date().toISOString(),
          })
          .eq('id', id)

        if (error) {
          console.error('Error anulando APU en Supabase:', error)
        }
      } catch (err) {
        console.error('Error anulando APU:', err)
      }
    })()
  }, [])

  // Duplicar APU
  const duplicarApu = useCallback(
    (id: string) => {
      const original = apus.find((a) => a.id === id)
      if (!original) return null
      return crearApu({
        ...original,
        id: crypto.randomUUID(),
        codigo: `${original.codigo}-COPIA`,
        nombre: `${original.nombre} (Copia)`,
      })
    },
    [apus, crearApu]
  )

  // APUs filtrados y ordenados predeciblemente
  const apusFiltrados = useMemo(() => {
    const q = busqueda.toLowerCase().trim()
    const filtrados = apus.filter((apu) => {
      if (apu.activo === false) return false
      const coincideCat =
        categoriaSeleccionada === 'todas' || apu.categoria === categoriaSeleccionada
      if (!coincideCat) return false

      if (filtroProyecto === 'global' && apu.proyecto_id) return false
      if (filtroProyecto !== 'todos' && filtroProyecto !== 'global' && apu.proyecto_id !== filtroProyecto) {
        return false
      }

      if (!q) return true
      return (
        apu.nombre.toLowerCase().includes(q) ||
        apu.codigo.toLowerCase().includes(q) ||
        (apu.descripcion && apu.descripcion.toLowerCase().includes(q)) ||
        (apu.proyecto_nombre && apu.proyecto_nombre.toLowerCase().includes(q))
      )
    })

    return filtrados.sort((a, b) => {
      if (criterioOrden === 'codigo') {
        return a.codigo.localeCompare(b.codigo, undefined, { numeric: true, sensitivity: 'base' })
      }
      if (criterioOrden === 'categoria') {
        const c = a.categoria.localeCompare(b.categoria)
        if (c !== 0) return c
        return a.codigo.localeCompare(b.codigo, undefined, { numeric: true, sensitivity: 'base' })
      }
      if (criterioOrden === 'precio_desc') {
        return b.precio_unitario_total - a.precio_unitario_total
      }
      if (criterioOrden === 'precio_asc') {
        return a.precio_unitario_total - b.precio_unitario_total
      }
      return 0
    })
  }, [apus, busqueda, categoriaSeleccionada, filtroProyecto, criterioOrden])

  return {
    apus,
    apusFiltrados,
    cargando,
    busqueda,
    setBusqueda,
    categoriaSeleccionada,
    setCategoriaSeleccionada,
    criterioOrden,
    setCriterioOrden,
    filtroProyecto,
    setFiltroProyecto,
    siguienteCodigo,
    crearApu,
    actualizarApu,
    actualizarAiuApu,
    eliminarApu,
    duplicarApu,
    sincronizarDesdeServidor,
  }
}

